import { FC, useEffect, useRef, useState, useCallback } from 'react';

interface VideoPlayerProps {
  src: string;
  thumbnail?: string | null;
  title?: string | null;
  onProgressUpdate: (percent: number) => void;
  initialPercent?: number;
  onError?: () => void;
}

const PLAYBACK_RATES = [0.5, 1, 1.25, 1.5, 2];
const PROGRESS_REPORT_INTERVAL = 30_000; // 30 seconds

const VideoPlayer: FC<VideoPlayerProps> = ({
  src,
  thumbnail,
  title,
  onProgressUpdate,
  initialPercent = 0,
  onError,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasSeekInitial = useRef(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showRateMenu, setShowRateMenu] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const hideControlsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Report progress periodically
  const reportProgress = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const percent = Math.round((video.currentTime / video.duration) * 100);
    onProgressUpdate(percent);
  }, [onProgressUpdate]);

  // Start/stop periodic reporting
  useEffect(() => {
    if (isPlaying) {
      progressIntervalRef.current = setInterval(reportProgress, PROGRESS_REPORT_INTERVAL);
    } else {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      reportProgress(); // Report on pause too
    }
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [isPlaying, reportProgress]);

  // Seek to initial percent when video metadata loads
  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setDuration(video.duration);
    setIsLoaded(true);

    if (initialPercent > 0 && !hasSeekInitial.current) {
      video.currentTime = (initialPercent / 100) * video.duration;
      hasSeekInitial.current = true;
    }
  }, [initialPercent]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (video) setCurrentTime(video.currentTime);
  }, []);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    video.currentTime = percent * duration;
  }, [duration]);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val === 0;
      setIsMuted(val === 0);
    }
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  }, []);

  const changeRate = useCallback((rate: number) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
      setPlaybackRate(rate);
    }
    setShowRateMenu(false);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      container.requestFullscreen();
    }
  }, []);

  // Track fullscreen changes
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Auto-hide controls
  const resetControlsTimer = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
    if (isPlaying) {
      hideControlsTimer.current = setTimeout(() => setShowControls(false), 3000);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!isPlaying) setShowControls(true);
  }, [isPlaying]);

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const volumeIcon = isMuted || volume === 0
    ? 'volume_off'
    : volume < 0.5
      ? 'volume_down'
      : 'volume_up';

  return (
    <div
      ref={containerRef}
      className="relative aspect-video bg-black rounded-xl overflow-hidden group select-none"
      onMouseMove={resetControlsTimer}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      onContextMenu={(e) => e.preventDefault()}
      data-testid="video-player"
    >
      {/* Title overlay */}
      {title && showControls && (
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 to-transparent px-4 py-3 z-10 pointer-events-none">
          <p className="text-white text-sm font-medium truncate">{title}</p>
        </div>
      )}

      {/* Video element */}
      <video
        ref={videoRef}
        src={src}
        poster={thumbnail || undefined}
        className="w-full h-full object-contain cursor-pointer"
        onClick={togglePlay}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false);
          reportProgress();
        }}
        onError={() => onError?.()}
        controlsList="nodownload"
        playsInline
        preload="metadata"
      />

      {/* Loading overlay */}
      {!isLoaded && thumbnail && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
          <span className="material-symbols-outlined text-white text-5xl animate-pulse">play_circle</span>
        </div>
      )}

      {/* Play/Pause center overlay on click */}
      {!isPlaying && isLoaded && (
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center z-10 cursor-pointer"
          data-testid="play-overlay"
        >
          <span className="material-symbols-outlined text-white/90 hover:text-white transition-colors"
            style={{ fontSize: 64, fontVariationSettings: "'FILL' 1" }}>
            play_circle
          </span>
        </button>
      )}

      {/* Controls bar */}
      <div
        className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 pb-2 pt-8 z-20 transition-opacity duration-300 ${
          showControls || !isPlaying ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Progress bar */}
        <div
          className="w-full h-1.5 bg-white/20 rounded-full cursor-pointer mb-2 group/progress hover:h-2.5 transition-all"
          onClick={handleSeek}
          data-testid="progress-bar"
        >
          <div
            className="h-full bg-accent rounded-full relative"
            style={{ width: `${progressPercent}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover/progress:opacity-100 transition-opacity" />
          </div>
        </div>

        {/* Controls row */}
        <div className="flex items-center gap-2 text-white">
          {/* Play/Pause */}
          <button onClick={togglePlay} className="hover:text-accent transition-colors" data-testid="play-button">
            <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
              {isPlaying ? 'pause' : 'play_arrow'}
            </span>
          </button>

          {/* Time */}
          <span className="text-xs text-white/70 min-w-[80px]" data-testid="time-display">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Volume */}
          <div className="flex items-center gap-1 group/vol">
            <button onClick={toggleMute} className="hover:text-accent transition-colors">
              <span className="material-symbols-outlined text-xl">{volumeIcon}</span>
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              className="w-0 group-hover/vol:w-16 transition-all duration-200 accent-accent cursor-pointer"
            />
          </div>

          {/* Playback speed */}
          <div className="relative">
            <button
              onClick={() => setShowRateMenu(!showRateMenu)}
              className="text-xs font-medium px-1.5 py-0.5 rounded bg-white/10 hover:bg-white/20 transition-colors"
              data-testid="speed-button"
            >
              {playbackRate}x
            </button>
            {showRateMenu && (
              <div className="absolute bottom-8 right-0 bg-gray-900 border border-gray-700 rounded-lg py-1 min-w-[60px] z-30">
                {PLAYBACK_RATES.map((rate) => (
                  <button
                    key={rate}
                    onClick={() => changeRate(rate)}
                    className={`block w-full text-left px-3 py-1 text-xs hover:bg-white/10 transition-colors ${
                      rate === playbackRate ? 'text-accent font-bold' : 'text-white'
                    }`}
                  >
                    {rate}x
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Fullscreen */}
          <button onClick={toggleFullscreen} className="hover:text-accent transition-colors">
            <span className="material-symbols-outlined text-xl">
              {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
